import type { McpConsentRequest, McpConsentScope } from '../../types'

/** One eligible workspace is picked for the user; several need a choice. */
export const initialConsentOrganization = (
  consent: Pick<McpConsentRequest, 'organizations'>
): number | null =>
  consent.organizations.length === 1 ? consent.organizations[0].id : null

export const consentScopesFor = (
  consent: Pick<McpConsentRequest, 'organizations'>,
  organizationId: number | null
): McpConsentScope[] =>
  consent.organizations.find((org) => org.id === organizationId)?.scopes ?? []

export const canAllowConsent = (
  consent: Pick<McpConsentRequest, 'organizations'>,
  organizationId: number | null
) =>
  organizationId !== null &&
  consent.organizations.some((org) => org.id === organizationId)

/** `cursor://anysphere.cursor-retrieval/cb` → `anysphere.cursor-retrieval`. */
export const redirectHost = (redirectUri: string): string => {
  try {
    const url = new URL(redirectUri)

    return url.host || url.protocol.replace(/:$/, '')
  } catch {
    return redirectUri
  }
}

/** Core answers 404 for an expired or foreign request (A9); anything else is retryable. */
export const isConsentExpiredError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { response?: { status?: unknown } }).response?.status === 404

/** Schemes that would run or read content in Account's own origin. */
const UNSAFE_REDIRECT_PROTOCOLS: ReadonlySet<string> = new Set([
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'blob:',
  'about:'
])

/**
 * Defence in depth on Core's `redirect_to`: http(s), loopback and app schemes
 * (`cursor:`, `vscode:`) pass; script/local schemes and non-absolute URLs do not.
 */
export const isSafeConsentRedirect = (redirectTo: string): boolean => {
  try {
    return !UNSAFE_REDIRECT_PROTOCOLS.has(new URL(redirectTo).protocol)
  } catch {
    return false
  }
}
